import { ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UserNotificationCenter as TenantUserNotificationCenter, Notification as TenantNotification, UserNotificationCenter } from '@Core/models/codegen/configuration.models';
import { TenantContext } from '@Core/contexts/tenant-context';
import { AuthService } from '@Services/auth.service';
import _ from 'lodash';
import { combineLatest } from 'rxjs';
import { AccountsService as AccountServiceExt } from '@Core/services/account-accounts.service';
import { AccountsService } from '@Core/services/codegen/accounts.service';
import { TenantService } from '@Core/services/codegen/tenant.service';
import { UserNotificationCenter as AccountUserNotificationCenter, Notification as AccountNotification } from '@Core/models/codegen/area.models';
import { UserContext } from '@Core/contexts/user-context';
import { NotificationCardVM, NotificationDomain, NotificationSubtitleVM } from '../sm-global-notifications/sm-global-notifications-card/sm-global-notifications-card.component';
import { TenantAssignableService } from '@Services/tenant-assignable.service';
import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { RouteEntryItem, RouteUtils } from '@Core/utils/route-utils';
import { NotificationType } from '@Core/enums/codegen/library.enums';
import { SmNomenclaturePipe } from '@Shared/Pipes/sm-nomenclature.pipe';
import { AsyncPipe } from '@angular/common';
import { GuidReplacerPipe } from '@Work/Pipes/guid-replacer.pipe';
import { TenantTenantService } from '@Services/tenant-tenant.service';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
    standalone: false,
    selector: 'sm-notification-icon-button',
    templateUrl: './sm-notification-icon-button.component.html',
    styleUrls: ['./sm-notification-icon-button.component.scss']
})
export class SmNotificationIconButtonComponent implements OnInit {

    private hasLoadedAccountNotifications: boolean = false;
    private hasLoadedTenantNotifications: boolean = false;
    private notifications: (TenantNotification | AccountNotification)[] = [];
    public notificationCenter: UserNotificationCenter;
    public notificationCardVMs: NotificationCardVM[] = [];
    public asyncPipe: AsyncPipe;
    private guidReplacerPipe: GuidReplacerPipe;
    private hasAccessToConfigure: boolean = false;
    private smNomenclaturePipe: SmNomenclaturePipe;

    @ViewChild('notifyPopover', { read: ElementRef }) public notifyPopover: ElementRef<HTMLElement>;

    constructor(
        private router: Router,
        private authService: AuthService,
        private tenantService: TenantService,
        private accountService: AccountsService,
        private accountServiceExt: AccountServiceExt,
        private tenantTenantService: TenantTenantService,
        private tenantContext: TenantContext,
        private userContext: UserContext,
        private tenantAssignableService: TenantAssignableService,
        private permissionsService: NgxPermissionsService,
        private cdRef: ChangeDetectorRef,
        private destroyRef: DestroyRef
    ) {
        this.asyncPipe = new AsyncPipe(this.cdRef);
    }

    ngOnInit() {
        this.guidReplacerPipe = new GuidReplacerPipe(this.tenantContext, this.tenantAssignableService, this.tenantTenantService);
        this.smNomenclaturePipe = new SmNomenclaturePipe(this.tenantContext);

        this.permissionsService.hasPermission([
            TenantPermissions.Common_SystemAccess,
            TenantPermissions.Common_Tenant,
            TenantPermissions.Common_Programs,
        ]).then((result) => {
            this.hasAccessToConfigure = result;
            this.buildNotificationCardVMs();
        });

        this.getTenantUserNotifications();

        combineLatest([
            this.userContext.getStore<TenantUserNotificationCenter>(new TenantUserNotificationCenter()).values,
            this.userContext.getStore<AccountUserNotificationCenter>(new AccountUserNotificationCenter()).values
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([tenantCenters, accountCenters]) => {
                this.notifications = [];

                _.forEach(tenantCenters[0]?.IsAlertedBy() || [], (edge) => this.notifications.push(edge.IsAlertedBy()));
                _.forEach(accountCenters[0]?.IsAlertedBy() || [], (edge) => this.notifications.push(edge.IsAlertedBy()));

                this.notificationCardVMs = this.buildNotificationCardVMs();
                this.notificationCenter = tenantCenters[0];
            });
    }

    private buildNotificationCardVMs(): NotificationCardVM[] {
        const vms: NotificationCardVM[] = this.notifications.map(notification => {

            var domain = notification instanceof TenantNotification ? NotificationDomain.Tenant : NotificationDomain.Account;

            var programId = notification?.ContextIds ? notification?.ContextIds["Program"] : null;

            var lastSubtitleNum = _.max(_.map(notification.Subtitles, s => s.OrderNum));
            const subtitles: NotificationSubtitleVM[] = notification.Subtitles.map((subtitle: NotificationSubtitle) => {
                var noConfigLink = domain == NotificationDomain.Tenant && !this.hasAccessToConfigure;

                return {
                    EntityType: subtitle.EntityType,
                    DisplayEntityType: this.asyncPipe.transform(this.smNomenclaturePipe.transform(subtitle.EntityType, programId)),
                    EntityText: subtitle.EntityText,
                    IsLink: !noConfigLink && subtitle.OrderNum === lastSubtitleNum
                };
            });

            return {
                Id: notification.Id,
                NotificationType: <NotificationType>notification.NotificationType,
                Domain: domain,
                Message: this.asyncPipe.transform(this.guidReplacerPipe.transform(notification.Message)),
                Subtitles: subtitles,
                Description: notification.Description,
                ContextIds: notification.ContextIds,
                OriginatorBadge: this.tenantAssignableService.getAssignableAsBadge(notification.IdentityKey),
                CreatedOnDate: new Date(notification?.data?.CreatedTimeUTC),
                IsRead: notification.Alerts()[0].Read,
                IsMarkedForDelete: null
            };
        })

        return vms;
    }

    public onClickLink(vm: NotificationCardVM) {
        this.handleReadNotifications(vm);

        if (vm.NotificationType == NotificationType.POLICY_EXPORT
            && vm.Subtitles && vm.Subtitles[0].EntityType == 'Download') {
            this.accountServiceExt.downloadPolicyExport(vm.Subtitles[0].EntityText);
        } else {

            var subtitle = _.find(vm.Subtitles, s => { return s.IsLink; });
            var entries: RouteEntryItem[] = _.map(Object.getOwnPropertyNames(vm.ContextIds), entityType => {
                var entityId = vm.ContextIds[entityType];
                var entry: RouteEntryItem = {
                    Type: entityType,
                    Id: entityId
                };
                return entry;
            });

            var url = RouteUtils.BuildEntityRoute(subtitle.EntityType, entries)
            this.router.navigate([url]);
        }

    }

    private getTenantUserNotifications(): void {
        if (!this.hasLoadedTenantNotifications) {
            this.tenantService.ListUserNotificationCenters(this.userContext);
            this.hasLoadedTenantNotifications = true;
        }

        if (this.authService.getAreaKey() && !this.hasLoadedAccountNotifications) {
            this.accountService.ListUserNotificationCenters(this.userContext);
            this.hasLoadedAccountNotifications = true;
        }
    }

    public closeNotifications() {
        const popoverElement = this.notifyPopover?.nativeElement as HTMLElement & { hidePopover?: () => void };
        popoverElement?.hidePopover?.();
    }

    public onNotificationsPopoverToggle(event: ToggleEvent): void {
        if (event.newState === 'closed') {
            this.handleDeletedNotifications();
        }
    }

    public handleDeletedNotifications(): void {
        let deletedAccountAlerts: string[] = [];
        let deletedTenantAlerts: string[] = [];
        this.notificationCardVMs.forEach(notificationCardVM => {
            if (notificationCardVM.IsMarkedForDelete) {
                if (notificationCardVM.Domain == NotificationDomain.Account) {
                    deletedAccountAlerts.push(notificationCardVM.Id);
                } else if (notificationCardVM.Domain == NotificationDomain.Tenant) {
                    deletedTenantAlerts.push(notificationCardVM.Id);
                }
            }
        });

        if (deletedAccountAlerts.length > 0) {
            this.accountService.RemoveNotificationsForUser(deletedAccountAlerts, this.userContext);
        }
        if (deletedTenantAlerts.length > 0) {
            this.tenantService.RemoveNotificationsForUser(deletedTenantAlerts, this.userContext);
        }

        this.closeNotifications();
    }

    public handleReadNotifications(notificationVM: NotificationCardVM = null): void {
        if (notificationVM) {
            if (notificationVM.Domain == NotificationDomain.Account)
                this.accountService.MarkNotificationsAsRead([notificationVM.Id], this.userContext);
            if (notificationVM.Domain == NotificationDomain.Tenant)
                this.tenantService.MarkNotificationsAsRead([notificationVM.Id], this.userContext);
        }
        else {
            this.markAllNotificationsAsRead();
        }

        this.closeNotifications();
    }

    public markAllNotificationsAsRead(): void {
        const accountReadNotifications: string[] = [];
        const tenantReadNotifications: string[] = [];

        this.notificationCardVMs.forEach(notification => {
            if (notification.IsRead)
                return;

            notification.IsRead = true;

            if (notification.Domain == NotificationDomain.Account)
                accountReadNotifications.push(notification.Id);
            if (notification.Domain == NotificationDomain.Tenant)
                tenantReadNotifications.push(notification.Id);
        });

        if (accountReadNotifications.length > 0)
            this.accountService.MarkNotificationsAsRead(accountReadNotifications, this.userContext);
        if (tenantReadNotifications.length > 0)
            this.tenantService.MarkNotificationsAsRead(tenantReadNotifications, this.userContext);
    }

    public getUnreadNotificationsCount(): number {
        if (!this.notificationCardVMs)
            return 0;

        return this.notificationCardVMs.filter(notification => notification.IsRead === false).length;
    };
}

export interface NotificationSubtitle {
    EntityType: string,
    EntityText: string,
    OrderNum: number
}
